import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ExternalLink, Sparkles, AlertCircle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [configuredSystems, setConfiguredSystems] = useState([]);
  const [assignedAgentsMap, setAssignedAgentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    ticket_number: '',
    source: 'servicenow',
    title: '',
    description: '',
    priority: 'medium',
    requester_name: '',
    requester_email: '',
    sap_system: 'S/4HANA Cloud',
    target_system_id: '',
    target_system_name: '',
    customization_type: 'Enhancement',
    is_error_ticket: false,
    error_code: '',
    error_message: '',
    error_module: ''
  });

  useEffect(() => {
    fetchTickets();
    fetchConfiguredSystems();
  }, []);

  const fetchConfiguredSystems = async () => {
    try {
      const response = await axios.get(`${API}/settings/sap-systems`);
      setConfiguredSystems(response.data);
    } catch (error) {
      console.error('Error fetching configured systems:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`);
      setTickets(response.data);
      
      // Fetch assigned agents for each ticket
      const agentPromises = response.data.map(async (ticket) => {
        try {
          const agentsRes = await axios.get(`${API}/agent-studio/tickets/${ticket.id}/agents`);
          return { ticketId: ticket.id, agents: agentsRes.data };
        } catch (err) {
          return { ticketId: ticket.id, agents: [] };
        }
      });
      
      const agentsData = await Promise.all(agentPromises);
      const agentsMap = {};
      agentsData.forEach(({ ticketId, agents }) => {
        agentsMap[ticketId] = agents;
      });
      setAssignedAgentsMap(agentsMap);
      
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const createMockTicket = async () => {
    try {
      const response = await axios.post(`${API}/tickets/mock`);
      console.log('Mock ticket created:', response.data);
      alert(`✅ Mock ticket created: ${response.data.ticket_number}`);
      await fetchTickets();
    } catch (error) {
      console.error('Error creating mock ticket:', error);
      alert(`❌ Error creating mock ticket: ${error.response?.data?.detail || error.message}`);
    }
  };

  const createTicket = async () => {
    try {
      await axios.post(`${API}/tickets`, formData);
      setOpen(false);
      fetchTickets();
      setFormData({
        ticket_number: '',
        source: 'servicenow',
        title: '',
        description: '',
        priority: 'medium',
        requester_name: '',
        requester_email: '',
        sap_system: 'S/4HANA Cloud',
        target_system_id: '',
        target_system_name: '',
        customization_type: 'Enhancement'
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleSystemSelection = (systemId) => {
    const selectedSystem = configuredSystems.find(s => s.id === systemId);
    setFormData({
      ...formData,
      target_system_id: systemId,
      target_system_name: selectedSystem ? selectedSystem.name : '',
      sap_system: selectedSystem ? selectedSystem.type : formData.sap_system
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-gray-500',
      'in_analysis': 'bg-blue-500',
      'in_design': 'bg-purple-500',
      'in_development': 'bg-indigo-500',
      'in_testing': 'bg-amber-500',
      'in_deployment': 'bg-orange-500',
      'in_governance': 'bg-cyan-500',
      'completed': 'bg-green-500',
      'rejected': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'border-gray-400 text-gray-600',
      'medium': 'border-blue-400 text-blue-600',
      'high': 'border-orange-400 text-orange-600',
      'critical': 'border-red-400 text-red-600'
    };
    return colors[priority] || 'border-gray-400 text-gray-600';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tickets</h1>
          <p className="text-muted-foreground mt-2">Manage customization requests</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={createMockTicket} variant="outline" data-testid="create-mock-ticket-btn">
            Generate Mock Ticket
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-ticket-btn">
                <Plus className="mr-2 h-4 w-4" /> Create Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Ticket</DialogTitle>
                <DialogDescription>Create a new SAP customization request</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ticket_number">Ticket Number</Label>
                    <Input id="ticket_number" value={formData.ticket_number} onChange={(e) => setFormData({...formData, ticket_number: e.target.value})} placeholder="INC123456" />
                  </div>
                  <div>
                    <Label htmlFor="source">Source</Label>
                    <Select value={formData.source} onValueChange={(value) => setFormData({...formData, source: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="servicenow">ServiceNow</SelectItem>
                        <SelectItem value="jira">Jira</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input id="title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Custom Report Development" />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Detailed description of the customization request" rows={4} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="customization_type">Customization Type</Label>
                    <Select value={formData.customization_type} onValueChange={(v) => setFormData({...formData, customization_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Enhancement">Enhancement</SelectItem>
                        <SelectItem value="Custom Report">Custom Report</SelectItem>
                        <SelectItem value="Fiori App">Fiori App</SelectItem>
                        <SelectItem value="SAP Error">SAP Error Resolution</SelectItem>
                        <SelectItem value="Integration">Integration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Error Ticket Fields (Conditional) */}
                {formData.customization_type === 'SAP Error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      SAP Error Details
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Error Code</Label>
                        <Input
                          value={formData.error_code}
                          onChange={(e) => setFormData({...formData, error_code: e.target.value, is_error_ticket: true})}
                          placeholder="e.g., F5 512, M7 301"
                        />
                      </div>
                      <div>
                        <Label>SAP Module</Label>
                        <Select value={formData.error_module} onValueChange={(v) => setFormData({...formData, error_module: v})}>
                          <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="security">Security</SelectItem>
                            <SelectItem value="sales">Sales & Distribution</SelectItem>
                            <SelectItem value="materials_management">Materials Management</SelectItem>
                            <SelectItem value="production">Production Planning</SelectItem>
                            <SelectItem value="finance_controlling">Finance & Controlling</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Complete Error Message</Label>
                      <Textarea
                        value={formData.error_message}
                        onChange={(e) => setFormData({...formData, error_message: e.target.value})}
                        placeholder="Paste the complete error message from SAP..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}
                
                {/* Target SAP System Selector */}
                <div>
                  <Label htmlFor="target_system">Target SAP System for Deployment *</Label>
                  {configuredSystems.length > 0 ? (
                    <Select value={formData.target_system_id} onValueChange={handleSystemSelection}>
                      <SelectTrigger id="target_system">
                        <SelectValue placeholder="Select SAP system where this will be deployed" />
                      </SelectTrigger>
                      <SelectContent>
                        {configuredSystems.map((system) => (
                          <SelectItem key={system.id} value={system.id}>
                            <div className="flex items-center gap-2">
                              {system.name} ({system.environment}) - {system.type}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-sm text-muted-foreground p-3 border rounded bg-amber-50">
                      No SAP systems configured. Go to Settings → SAP Landscape to add systems.
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Select the SAP system where this customization will be developed and deployed
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sap_system">SAP System Type (Legacy)</Label>
                    <Select value={formData.sap_system} onValueChange={(value) => setFormData({...formData, sap_system: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="S/4HANA Cloud">S/4HANA Cloud</SelectItem>
                        <SelectItem value="S/4HANA On-Premise">S/4HANA On-Premise</SelectItem>
                        <SelectItem value="SAP BTP">SAP BTP</SelectItem>
                        <SelectItem value="SAP ECC">SAP ECC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="requester_name">Requester Name</Label>
                    <Input id="requester_name" value={formData.requester_name} onChange={(e) => setFormData({...formData, requester_name: e.target.value})} placeholder="John Doe" />
                  </div>
                  <div>
                    <Label htmlFor="requester_email">Requester Email</Label>
                    <Input id="requester_email" type="email" value={formData.requester_email} onChange={(e) => setFormData({...formData, requester_email: e.target.value})} placeholder="john@company.com" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="customization_type">Customization Type</Label>
                  <Input id="customization_type" value={formData.customization_type} onChange={(e) => setFormData({...formData, customization_type: e.target.value})} placeholder="Enhancement, Custom Report, etc." />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={createTicket} data-testid="submit-ticket-btn">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {tickets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">No tickets found. Create your first ticket to get started.</p>
            <Button onClick={createMockTicket}>Generate Mock Ticket</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket) => (
            <Card key={ticket.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)} data-testid={`ticket-card-${ticket.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                      <Badge variant="outline" className="text-xs">{ticket.source}</Badge>
                      <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>{ticket.status.replace('_', ' ')}</Badge>
                      <Badge variant="outline" className={`text-xs border ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</Badge>
                    </div>
                    <CardTitle className="text-xl">{ticket.title}</CardTitle>
                    <CardDescription className="mt-2">{ticket.description}</CardDescription>
                  </div>
                  <ExternalLink className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Target System</div>
                      <div className="font-medium">
                        {ticket.target_system_name || ticket.sap_system}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Type</div>
                      <div className="font-medium">{ticket.customization_type}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Requester</div>
                      <div className="font-medium">{ticket.requester_name}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Created</div>
                      <div className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  
                  {/* Assigned Agents from Agent Studio */}
                  {assignedAgentsMap[ticket.id] && assignedAgentsMap[ticket.id].length > 0 && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1">Custom Agents:</div>
                      <div className="flex flex-wrap gap-1">
                        {assignedAgentsMap[ticket.id].map((assignment, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            <Sparkles className="h-3 w-3 mr-1" />
                            {assignment.agent_name}
                            {assignment.status === 'completed' && ' ✓'}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
